import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate, useParams } from 'react-router-dom'
import { get, post } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import PageLoader from '../../../../components/loader/PageLoader'
import CancelBooking from './CancelBooking'
import FormLoader from '../../../../components/loader/FormLoader'
import moment from 'moment'
import TagPicker from '../../../../components/tags/TagPicker'
import SenderIdAttach from '../../../../components/senderId/SenderIdAttach'
import DownloadAwb from '../../../../components/awb/DownloadAwb'
import {
  ID_TYPE_LABELS,
  canViewShipperIds,
} from '../shipperIds/shared'
import './Booking.scss'

const money = (value: any) =>
  `₦${parseFloat(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const DELIVERY_TYPE_META: Record<string, { label: string; icon: string }> = {
  CUSTOMER: { label: 'Sender', icon: 'mdi:account-arrow-right-outline' },
  RECIEVER: { label: 'Receiver', icon: 'mdi:account-arrow-left-outline' },
  RECEIVER: { label: 'Receiver', icon: 'mdi:account-arrow-left-outline' },
}

function Booking() {
  const { id } = useParams()
  const { notify, profile } = useGlobalContext()
  const [bookingData, setBookingData] = useState<any>(null)
  const [showCancel, setShowCancel] = useState(false)
  const [tab, setTab] = useState('items')
  const [fileName, setFileName] = useState('')
  const [bookingLoading, setBookingLoading] = useState(true)
  const [loadingAction, setLoadingAction] = useState<string | null>(null)
  const navigate = useNavigate()

  // `creatorName` on the booking is whoever filled the form — an agent
  // booking on a customer's behalf included — so it is not safe to show as
  // "the account this booking belongs to". When there is a userId, resolve
  // the actual customer record the same way the customer page does.
  const [accountCustomer, setAccountCustomer] = useState<any>(null)
  const [accountLoading, setAccountLoading] = useState(false)

  const fetchAccountCustomer = async (userId: string) => {
    setAccountLoading(true)
    get(`user/view/${userId}`)
      .then((result: any) => {
        setAccountCustomer(result.status === 200 ? result.data : null)
        setAccountLoading(false)
      })
      .catch(() => {
        setAccountCustomer(null)
        setAccountLoading(false)
      })
  }

  const fetchBooking = async () => {
    get(`admin/booking/${id}`)
      .then((result: any) => {
        if (result.status === 200) {
          setBookingData(result.data)
          setFileName(
            `${result.data.delivery_info?.[0]?.contactInformation?.fullName}-${result.data.shipmentMeta?.trackingId}.pdf`,
          )
          if (result.data.shipmentMeta?.trackingId) {
            fetchTracking(result.data.shipmentMeta.trackingId)
          } else {
            setTrackingLoading(false)
          }
          // A business-created booking's "account" is the business itself —
          // creatorName already holds that name. Only chase userId down to a
          // customer record for bookings actually tied to a customer account.
          if (
            result.data.userId &&
            String(result.data.createdBy || '').toLowerCase() !== 'business'
          ) {
            fetchAccountCustomer(result.data.userId)
          }
        } else {
          notify('error', result.er?.message)
        }
        setBookingLoading(false)
      })
      .catch(() => {
        setBookingLoading(false)
      })
  }
  useEffect(() => {
    fetchBooking()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  //<---------------Fetch Tracking Details---------------->
  const [trackingLoading, setTrackingLoading] = useState(true)
  const [trackingData, setTrackingData] = useState<any>(null)

  const fetchTracking = async (trackingId: string) => {
    setTrackingLoading(true)
    get(`tracking/single?id=${trackingId}`)
      .then((result: any) => {
        if (result.status === 200) {
          setTrackingData(result.data.shipments[0])
        } else {
          notify('error', result.er?.message)
        }
        setTrackingLoading(false)
      })
      .catch(() => {
        setTrackingLoading(false)
      })
  }

  const handleGenerate = async (type: 'receipt' | 'invoice') => {
    setLoadingAction(`generate-${type}`)
    try {
      const result: any = await post(
        'admin/booking/generate-documents',
        {
          bookingId: id,
          type,
        },
        null,
      )
      if (result.status === 200 || result.status === 201) {
        notify('success', result.message || `${type} generated successfully`)
        await fetchBooking()
      } else {
        notify('error', result.message || 'Failed to generate document')
      }
    } catch (error: any) {
      notify('error', error.message || 'An error occurred')
    } finally {
      setLoadingAction(null)
    }
  }

  const handleDownload = async (type: 'receipt' | 'invoice') => {
    setLoadingAction(`download-${type}`)
    try {
      const result: any = await get(
        `admin/booking/download-document?bookingId=${id}&type=${type}`,
      )
      if (result.status === 200 && result.data?.dataUrl) {
        const link = document.createElement('a')
        link.href = result.data.dataUrl
        link.download = result.data.fileName || `${type}-${id}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        notify('error', result.message || 'Failed to download document')
      }
    } catch (error: any) {
      notify('error', error.message || 'An error occurred')
    } finally {
      setLoadingAction(null)
    }
  }

  const copyToClipboard = (value: string) => {
    if (!value) return
    navigator.clipboard
      .writeText(value)
      .then(() => notify('success', 'Copied to clipboard'))
      .catch(() => notify('error', 'Could not copy'))
  }

  if (bookingLoading || !bookingData) {
    return (
      <div className='LoaderPage'>
        <PageLoader position='center' />
      </div>
    )
  }

  const status = bookingData.booking_status || {}
  const deliveryInfo: any[] = bookingData.delivery_info || []
  const sender = deliveryInfo.find((d) => d.type === 'CUSTOMER')
  const receiver = deliveryInfo.find(
    (d) => d.type === 'RECIEVER' || d.type === 'RECEIVER',
  )
  const extraStops = deliveryInfo.filter((d) => d !== sender && d !== receiver)

  const totalWeight = (bookingData.packages || []).reduce(
    (sum: number, p: any) => sum + (p.weight || 0),
    0,
  )

  const canManageStatus =
    status.code !== 'CCD' &&
    (profile?.role === 'super admin' ||
      profile?.role === 'customer service' ||
      status.code === 'AWD' ||
      status.code === 'SHC' ||
      status.code === 'OND' ||
      status.code === 'DRF')

  const breakDown = bookingData.meta?.breakDown || {}
  const priceCheck = bookingData.meta?.priceCheck
  const deliveryCapabilities = bookingData.meta?.deliveryCapabilities

  const renderAddress = (entry: any) => {
    const addr = entry?.postalAddress || {}
    const parts = [
      addr.addressLine1,
      addr.cityName,
      addr.countyName,
      addr.countryCode,
      addr.postalCode,
    ].filter((p) => p && String(p).trim().length > 0)
    return parts.join(', ') || 'Not provided'
  }

  const tabs = [
    { key: 'items', label: 'Specified Items', icon: 'mdi:package-variant-closed' },
    { key: 'packages', label: 'Packages', icon: 'mage:package-box-fill' },
    { key: 'pricing', label: 'Price Breakdown', icon: 'tabler:report-money' },
    { key: 'tracking', label: 'Tracking', icon: 'grommet-icons:map-location' },
    { key: 'documents', label: 'Shipment & Documents', icon: 'mdi:file-document-outline' },
  ]

  return (
    <div className='booking-details'>
      {showCancel && (
        <CancelBooking
          fetchBooking={fetchBooking}
          setShowCancel={setShowCancel}
          bookingData={bookingData}
        />
      )}

      <div className='top'>
        <div className='back-btn' onClick={() => navigate(-1)}>
          <Icon icon='ion:return-up-back-outline' />
          <h2>Back</h2>
        </div>
        <p className='booking-ref'>Booking ID: {bookingData._id}</p>
      </div>

      <div className='booking-wrap'>
        {/* ---------------- Status banner ---------------- */}
        <div className='status-banner'>
          <div className='banner-top'>
            <div className='status-icon'>
              <Icon icon='mdi:truck-fast-outline' />
            </div>
            <div className='status-info'>
              <div className='tracking-row'>
                <h1>{bookingData.shipmentMeta?.trackingId || 'No tracking ID yet'}</h1>
                {bookingData.shipmentMeta?.trackingId && (
                  <button
                    className='copy-btn'
                    onClick={() => copyToClipboard(bookingData.shipmentMeta.trackingId)}
                    title='Copy tracking ID'
                  >
                    <Icon icon='mdi:content-copy' />
                  </button>
                )}
              </div>
              {bookingData.userId &&
              String(bookingData.createdBy || '').toLowerCase() !== 'business' ? (
                <div
                  className='account-row link'
                  onClick={() => navigate(`/admin/customer/${bookingData.userId}`)}
                  title='View customer account'
                >
                  <Icon icon='mdi:account-circle-outline' />
                  {accountLoading
                    ? 'Loading account…'
                    : accountCustomer
                      ? `${accountCustomer.firstname || ''} ${accountCustomer.lastname || ''}`.trim() ||
                        'Unnamed customer'
                      : 'Customer account'}
                </div>
              ) : (
                bookingData.creatorName && (
                  <div className='account-row'>
                    <Icon icon='mdi:domain' />
                    {bookingData.creatorName}
                  </div>
                )
              )}
              <div className='meta'>
                <span>
                  <Icon icon='mdi:package-variant' />
                  {bookingData.shipment_type}
                </span>
                <span>
                  <Icon icon='mdi:swap-horizontal' />
                  {bookingData.deliveryType}
                </span>
                <span>
                  <Icon icon='mdi:calendar-outline' />
                  {moment(bookingData.createdAt).format('Do MMM YYYY, h:mma')}
                </span>
              </div>
            </div>
            <div className='banner-right'>
              <div className='charge-display'>
                <p>Charge</p>
                <h2>{money(bookingData.charge)}</h2>
              </div>
              <span className={`chip ${status.code}`}>{status.description}</span>
            </div>
          </div>

          <div className='banner-actions'>
            {status.code !== 'CCD' && (
              <DownloadAwb
                awb={bookingData.awb}
                endpoint={`admin/booking/${id}/awb`}
                fileName={fileName}
                onReleased={fetchBooking}
              />
            )}
            {canManageStatus && (
              <button onClick={() => setShowCancel(true)}>
                <Icon icon='mdi:pencil-outline' />
                Update Booking
              </button>
            )}
            <button
              onClick={() =>
                navigate(`/admin/${bookingData.deliveryType?.toLowerCase()}/${id}`)
              }
            >
              <Icon icon='mdi:content-duplicate' />
              Duplicate Booking
            </button>
            {status.code !== 'CCD' &&
              (bookingData.receiptNumber ? (
                <button
                  onClick={() => handleDownload('receipt')}
                  disabled={loadingAction === 'download-receipt'}
                >
                  <Icon icon='mdi:receipt-text-outline' />
                  {loadingAction === 'download-receipt' ? 'Downloading…' : 'Download Receipt'}
                </button>
              ) : (
                <button
                  onClick={() => handleGenerate('receipt')}
                  disabled={loadingAction === 'generate-receipt'}
                >
                  <Icon icon='mdi:receipt-text-outline' />
                  {loadingAction === 'generate-receipt' ? 'Generating…' : 'Generate Receipt'}
                </button>
              ))}
            {status.code !== 'CCD' &&
              (bookingData.invoiceNumber ? (
                <button
                  onClick={() => handleDownload('invoice')}
                  disabled={loadingAction === 'download-invoice'}
                >
                  <Icon icon='mdi:file-document-outline' />
                  {loadingAction === 'download-invoice' ? 'Downloading…' : 'Download Invoice'}
                </button>
              ) : (
                <button
                  onClick={() => handleGenerate('invoice')}
                  disabled={loadingAction === 'generate-invoice'}
                >
                  <Icon icon='mdi:file-document-outline' />
                  {loadingAction === 'generate-invoice' ? 'Generating…' : 'Generate Invoice'}
                </button>
              ))}
          </div>
        </div>

        {status.code === 'CCD' && bookingData.reason && (
          <div className='alert-banner danger'>
            <Icon icon='mdi:alert-circle-outline' />
            <p>
              <b>Cancellation reason:</b> {bookingData.reason}
            </p>
          </div>
        )}

        {priceCheck && priceCheck.status && priceCheck.status !== 'match' && (
          <div className='alert-banner warning'>
            <Icon icon='mdi:alert-outline' />
            <p>
              <b>Price mismatch flagged at booking time.</b> Client charged{' '}
              {money(priceCheck.clientCharge)}, server calculated{' '}
              {money(priceCheck.serverPrice)} (diff {money(priceCheck.diff)}).
            </p>
          </div>
        )}

        {/* ---------------- Route card ---------------- */}
        <div className='route-card'>
          <div className='route-row'>
            <div className='route-endpoint'>
              <div className='route-label'>
                <Icon icon='mdi:account-arrow-right-outline' />
                Sender
              </div>
              <h3>{sender?.contactInformation?.fullName || 'Not provided'}</h3>
              {sender?.contactInformation?.companyName && (
                <p className='company'>{sender.contactInformation.companyName}</p>
              )}
              <p className='address'>{renderAddress(sender)}</p>
              <div className='contact-row'>
                {sender?.contactInformation?.phone && (
                  <span>
                    <Icon icon='mdi:phone-outline' />
                    {sender.contactInformation.phone.trim()}
                  </span>
                )}
                {sender?.contactInformation?.email && (
                  <span>
                    <Icon icon='mdi:email-outline' />
                    {sender.contactInformation.email}
                  </span>
                )}
              </div>
            </div>

            <div className='route-connector'>
              <div className='line' />
              <Icon icon='mdi:arrow-right-thin' />
              <div className='line' />
            </div>

            <div className='route-endpoint'>
              <div className='route-label'>
                <Icon icon='mdi:account-arrow-left-outline' />
                Receiver
              </div>
              <h3>{receiver?.contactInformation?.fullName || 'Not provided'}</h3>
              {receiver?.contactInformation?.companyName && (
                <p className='company'>{receiver.contactInformation.companyName}</p>
              )}
              <p className='address'>{renderAddress(receiver)}</p>
              <div className='contact-row'>
                {receiver?.contactInformation?.phone && (
                  <span>
                    <Icon icon='mdi:phone-outline' />
                    {receiver.contactInformation.phone.trim()}
                  </span>
                )}
                {receiver?.contactInformation?.email && (
                  <span>
                    <Icon icon='mdi:email-outline' />
                    {receiver.contactInformation.email}
                  </span>
                )}
              </div>
            </div>
          </div>

          {extraStops.map((entry) => {
            const meta = DELIVERY_TYPE_META[entry.type] || {
              label: entry.type
                ? entry.type.charAt(0) + entry.type.slice(1).toLowerCase()
                : 'Other',
              icon: 'mdi:map-marker-outline',
            }
            return (
              <div className='route-extra' key={entry._id}>
                <div className='extra-icon'>
                  <Icon icon={meta.icon} />
                </div>
                <div>
                  <p>{meta.label} Contact</p>
                  <h4>
                    {entry.contactInformation?.fullName}
                    {entry.contactInformation?.companyName &&
                      ` — ${entry.contactInformation.companyName}`}
                  </h4>
                  <span>
                    {[entry.contactInformation?.phone?.trim(), entry.contactInformation?.email]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        {/* ---------------- Overview card ---------------- */}
        <div className='one'>
          <p className='section-title'>Overview</p>
          <div className='placed-cards'>
            <div className='placed-card'>
              <p>Delivery Type</p>
              <h2>{bookingData.deliveryType}</h2>
            </div>
            <div className='placed-card'>
              <p>Category</p>
              <h2>{bookingData.category || '—'}</h2>
            </div>
            <div className='placed-card'>
              <p>Items</p>
              <h2>{bookingData.number_items}</h2>
            </div>
            <div className='placed-card'>
              <p>Total Weight</p>
              <h2>{totalWeight} kg</h2>
            </div>
            <div className='placed-card'>
              <p>Declared Value</p>
              <h2>{money(bookingData.value)}</h2>
            </div>
            <div className='placed-card'>
              <p>Payment Method</p>
              <h2 className='capitalize'>{bookingData.paymentMethod || '—'}</h2>
            </div>
            <div className='placed-card'>
              <p>Channel</p>
              <h2 className='capitalize'>{bookingData.channel || '—'}</h2>
            </div>
            <div className='placed-card'>
              <p>Insurance</p>
              <h2>{bookingData.insurance ? 'Insured' : 'Not Insured'}</h2>
            </div>
            <div className='placed-card'>
              <p>Pickup</p>
              <h2>{bookingData.pickup ? 'Yes' : 'No'}</h2>
            </div>
            {deliveryCapabilities?.estimatedDeliveryDateAndTime && (
              <div className='placed-card'>
                <p>Estimated Delivery</p>
                <h2>
                  {moment(deliveryCapabilities.estimatedDeliveryDateAndTime).format(
                    'Do MMM YYYY',
                  )}
                </h2>
              </div>
            )}
            {typeof deliveryCapabilities?.totalTransitDays === 'number' && (
              <div className='placed-card'>
                <p>Transit Days</p>
                <h2>{deliveryCapabilities.totalTransitDays}</h2>
              </div>
            )}
          </div>

          <div className='details'>
            <div className='details-card-long'>
              <p>Description</p>
              <h3>{bookingData.description || '—'}</h3>
            </div>
            <div className='details-card'>
              <p>Location</p>
              <h3 className='capitalize'>{bookingData.location || '—'}</h3>
            </div>
            <div className='details-card'>
              <p>Created By</p>
              <h3 className='capitalize'>
                {bookingData.creatorName || bookingData.createdBy || '—'}
              </h3>
            </div>
            {bookingData.businessId && (
              <div className='details-card'>
                <p>Business ID</p>
                <h3>{bookingData.businessId}</h3>
              </div>
            )}
            {bookingData.reference && (
              <div className='details-card'>
                <p>Reference</p>
                <h3>{bookingData.reference}</h3>
              </div>
            )}
            <div className='details-card'>
              <p>Last Updated</p>
              <h3>{moment(bookingData.updatedAt).format('Do MMM YYYY, h:mma')}</h3>
            </div>
            <div className='details-card'>
              <p>Sender ID Verification</p>
              {id && (
                <SenderIdAttach
                  bookingId={id}
                  deliveryType={bookingData.deliveryType}
                  senderIdImage={bookingData.senderIdImage}
                  senderIdImageUrl={bookingData.senderIdImageUrl}
                  senderIsAccountOwner={bookingData.senderIsAccountOwner}
                  senderIdCheck={bookingData.senderIdCheck}
                  onChange={(next) =>
                    setBookingData((prev: any) => ({ ...prev, ...next }))
                  }
                />
              )}
            </div>
            {/* The ID the business registered for this shipper (Business API
                only). A snapshot — later re-uploads don't change it. */}
            {bookingData.channel === 'api' && (
              <div className='details-card'>
                <p>Shipper ID</p>
                {bookingData.customerIdentity?.identityId ? (
                  <>
                    <h3>{bookingData.customerIdentity.fullName}</h3>
                    <h3 className='shipper-id-number'>
                      {ID_TYPE_LABELS[bookingData.customerIdentity.idType] ||
                        bookingData.customerIdentity.idType}{' '}
                      ****{bookingData.customerIdentity.idNumberLast4} · v
                      {bookingData.customerIdentity.version}
                    </h3>
                    {canViewShipperIds(profile) && (
                      <a
                        href={`/admin/shipper-id/${bookingData.customerIdentity.identityId}`}
                        onClick={(e) => {
                          e.preventDefault()
                          navigate(
                            `/admin/shipper-id/${bookingData.customerIdentity.identityId}`,
                          )
                        }}
                      >
                        View ID
                      </a>
                    )}
                  </>
                ) : (
                  <h3>None on file</h3>
                )}
              </div>
            )}
            <div className='details-card-long'>
              <p>Tags</p>
              {id && (
                <TagPicker
                  kind='booking'
                  shipmentId={id}
                  value={(bookingData.tags || []).map((tag: any) =>
                    typeof tag === 'string' ? tag : tag._id,
                  )}
                  onChange={(tagIds) =>
                    setBookingData((prev: any) => ({ ...prev, tags: tagIds }))
                  }
                />
              )}
            </div>
          </div>
        </div>

        {/* ---------------- Tabbed detail card ---------------- */}
        <div className='two'>
          <div className='wrap-top flex justify-between items-center'>
            <div className='buttons'>
              {tabs.map((t) => (
                <button
                  key={t.key}
                  className={tab === t.key ? 'active' : ''}
                  onClick={() => setTab(t.key)}
                >
                  <Icon icon={t.icon} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {tab === 'items' && (
            <div className='tab-panel'>
              {bookingData.specifiedItems?.length > 0 ? (
                <div className='table-wrap'>
                  <table>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Item</th>
                        <th>Commodity Code</th>
                        <th>Qty</th>
                        <th>Net Weight</th>
                        <th>Gross Weight</th>
                        <th>Item Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingData.specifiedItems.map((item: any, i: number) => (
                        <tr key={item._id}>
                          <td>{i + 1}</td>
                          <td>
                            {item.itemName}
                            {item.description && item.description !== item.itemName && (
                              <small className='td-note'>{item.description}</small>
                            )}
                          </td>
                          <td>
                            {item.commodityCodes?.value}
                            {item.commodityCodes?.label && (
                              <small className='td-note'>{item.commodityCodes.label}</small>
                            )}
                          </td>
                          <td>{item.itemQuantity}</td>
                          <td>{item.netValue}</td>
                          <td>{item.grossValue}</td>
                          <td>{money(item.itemValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className='empty-state'>
                  <Icon icon='mdi:package-variant-closed-remove' />
                  <p>No specified items on this booking</p>
                </div>
              )}
            </div>
          )}

          {tab === 'packages' && (
            <div className='tab-panel'>
              <div className='table-wrap'>
                <table>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Weight</th>
                      <th>Length</th>
                      <th>Width</th>
                      <th>Height</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bookingData.packages || []).map((item: any, i: number) => (
                      <tr key={item._id}>
                        <td>{i + 1}</td>
                        <td>{item.weight} kg</td>
                        <td>{item.dimensions?.length}</td>
                        <td>{item.dimensions?.width}</td>
                        <td>{item.dimensions?.height}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'pricing' && (
            <div className='tab-panel'>
              {(breakDown.addOns?.length > 0 ||
                (breakDown.addOnsCost && Number(breakDown.addOnsCost) > 0)) && (
                <>
                  <div className='mini-tag'>
                    <h4>Add-ons</h4>
                  </div>
                  {breakDown.addOns?.length > 0 ? (
                    <div className='table-wrap'>
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Quantity</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakDown.addOns.map((addon: any, index: number) => (
                            <tr key={index}>
                              <td>{addon.name}</td>
                              <td>{addon.quantity}</td>
                              <td>{money(addon.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ paddingBottom: '20px', fontSize: '13px', color: '#6b7280' }}>
                      Add-ons cost included. Details unavailable.
                    </p>
                  )}
                </>
              )}

              <div className='mini-tag'>
                <h4>Summary</h4>
              </div>
              <div className='info-grid'>
                <div className='info-card'>
                  <p>Shipping Cost</p>
                  <h3>
                    {money(
                      breakDown.bookingCharge ??
                        breakDown.shippingCost ??
                        bookingData.meta?.shippingCost ??
                        0,
                    )}
                  </h3>
                </div>
                <div className='info-card'>
                  <p>Insurance</p>
                  <h3>{money(breakDown.insurance ?? bookingData.meta?.insurance ?? 0)}</h3>
                </div>
                {breakDown.addOnsCost > 0 && (
                  <div className='info-card'>
                    <p>Add-ons Cost</p>
                    <h3>{money(breakDown.addOnsCost)}</h3>
                  </div>
                )}
                {breakDown.discountAmount > 0 && (
                  <div className='info-card'>
                    <p>Discount</p>
                    <h3 style={{ color: '#16a34a' }}>-{money(breakDown.discountAmount)}</h3>
                  </div>
                )}
              </div>

              {breakDown.extraCharges?.length > 0 && (
                <>
                  <div className='mini-tag'>
                    <h4>Extra Charges</h4>
                  </div>
                  <div className='info-grid'>
                    {breakDown.extraCharges.map((charge: any, index: number) => (
                      <div className='info-card' key={index}>
                        <p>{charge.name}</p>
                        <h3>{money(charge.price)}</h3>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className='total-row'>
                <p>Total Charge</p>
                <h3>{money(bookingData.charge)}</h3>
              </div>
            </div>
          )}

          {tab === 'tracking' && (
            <div className='tab-panel'>
              {trackingLoading ? (
                <FormLoader position='center' />
              ) : trackingData?.events?.length > 0 ? (
                <div className='table-wrap'>
                  <table>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trackingData.events.map((item: any, i: number) => (
                        <tr key={i + 1}>
                          <td>{i + 1}</td>
                          <td>
                            {moment(item.date).format('Do MMM, YYYY')} at{' '}
                            {moment(item.time, 'HH:mm:ss').format('h:mma')}
                          </td>
                          <td>{item.description}</td>
                          <td>{item.serviceArea?.[0]?.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className='empty-state'>
                  <Icon icon='material-symbols-light:track-changes-rounded' />
                  <p>No tracking information to show</p>
                </div>
              )}
            </div>
          )}

          {tab === 'documents' && (
            <div className='tab-panel'>
              <div className='mini-tag'>
                <h4>Shipment</h4>
              </div>
              <div className='info-grid'>
                <div className='info-card'>
                  <p>Tracking ID</p>
                  <h3>{bookingData.shipmentMeta?.trackingId || '—'}</h3>
                </div>
                <div className='info-card'>
                  <p>Dispatch Confirmation No.</p>
                  <h3>{bookingData.shipmentMeta?.dispatchConfirmationNumber || '—'}</h3>
                </div>
                <div className='info-card'>
                  <p>Receipt Number</p>
                  <h3>{bookingData.receiptNumber || 'Not generated'}</h3>
                </div>
                <div className='info-card'>
                  <p>Invoice Number</p>
                  <h3>{bookingData.invoiceNumber || 'Not generated'}</h3>
                </div>
                {bookingData.shipmentMeta?.cancellationMeta?.checkCount > 0 && (
                  <div className='info-card'>
                    <p>Cancellation Checks</p>
                    <h3>{bookingData.shipmentMeta.cancellationMeta.checkCount}</h3>
                  </div>
                )}
              </div>

              {bookingData.shipmentMeta?.packages?.length > 0 && (
                <>
                  <div className='mini-tag'>
                    <h4>Package Tracking Numbers</h4>
                  </div>
                  <div className='table-wrap'>
                    <table>
                      <thead>
                        <tr>
                          <th>Ref No.</th>
                          <th>Tracking Number</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookingData.shipmentMeta.packages.map((pkg: any) => (
                          <tr key={pkg._id}>
                            <td>{pkg.referenceNumber}</td>
                            <td>{pkg.trackingNumber}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div className='mini-tag'>
                <h4>Airway Bill</h4>
              </div>
              <div className='info-grid'>
                <div className='info-card'>
                  <p>Released</p>
                  <h3 className={bookingData.awb?.released ? 'color-green' : ''}>
                    {bookingData.awb?.released ? 'Yes' : 'No'}
                  </h3>
                </div>
                {bookingData.awb?.releasedAt && (
                  <div className='info-card'>
                    <p>Released At</p>
                    <h3>{moment(bookingData.awb.releasedAt).format('Do MMM YYYY, h:mma')}</h3>
                  </div>
                )}
                {(bookingData.shipmentMeta?.awbReleasedByName || bookingData.awb?.releasedBy) && (
                  <div className='info-card'>
                    <p>Released By</p>
                    <h3>
                      {bookingData.shipmentMeta?.awbReleasedByName ||
                        bookingData.awb?.releasedBy}
                      {bookingData.shipmentMeta?.awbReleasedByRole &&
                        ` (${bookingData.shipmentMeta.awbReleasedByRole})`}
                    </h3>
                  </div>
                )}
                {bookingData.awb?.blockedReason && (
                  <div className='info-card'>
                    <p>Blocked Reason</p>
                    <h3 className='color-red'>{bookingData.awb.blockedReason}</h3>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Booking
